import { getNodeServiceDependencies } from '@/codeGeneration/dependenciesGetter';
import { V4UUID } from '@/firebase/database';
import {
  getLastIndexOf,
  toSnakeCase
} from '@/modules/globalModule/components/helperMethods';
import { useGlobalStore } from '@/modules/globalModule/store';
import {
  CustomNodeText,
  Node
} from '@/modules/nodesCanvasModule/components/storeClasses/node';
import { useNodeCanvasStore } from '@/modules/nodesCanvasModule/store';

/**
 * Processes the given node text. Uses the node canvas
 * @param node
 * @param customTextStr
 * @returns
 */
export const processCustomPythonNode = (
  node: Node,
  customTextStr: string,
  workspaceId: V4UUID
): CustomNodeText => {
  let customNodeText: CustomNodeText = {
    importText: '',
    constructorText: '',
    mainText: '',
    methodsText: '',
    callbacks: []
  };
  const globalStore = useGlobalStore();
  if (
    customTextStr.includes('# add custom imports below\n') &&
    customTextStr.includes('\n# end custom imports')
  ) {
    customNodeText.importText = customTextStr
      .slice(
        getLastIndexOf(customTextStr, '# add custom imports below\n'),
        customTextStr.indexOf('\n# end custom imports')
      )
      .trimEnd();
  }
  if (
    customTextStr.includes('# customize init function below\n') &&
    customTextStr.includes('\n        # end init customization')
  ) {
    customNodeText.constructorText = customTextStr
      .slice(
        getLastIndexOf(customTextStr, '# customize init function below\n'),
        customTextStr.indexOf('\n        # end init customization')
      )
      .trimEnd();
  }
  if (
    customTextStr.includes('# add custom methods to the node below\n') &&
    customTextStr.includes('\n    # end custom methods')
  ) {
    customNodeText.methodsText = customTextStr
      .slice(
        getLastIndexOf(
          customTextStr,
          '# add custom methods to the node below\n'
        ),
        customTextStr.indexOf('\n    # end custom methods')
      )
      .trimEnd();
  }

  if (
    customTextStr.includes('# customize main function below\n') &&
    customTextStr.includes('\n    # end main customization')
  ) {
    customNodeText.mainText = customTextStr
      .slice(
        getLastIndexOf(customTextStr, '# customize main function below\n'),
        customTextStr.indexOf('\n    # end main customization')
      )
      .trimEnd();
  }

  // getting topic subscription callback text
  if (node.subscribers) {
    for (let subId of node.subscribers) {
      let topic = globalStore.getTopic(subId);
      if (!topic) continue;
      if (
        customTextStr.includes(
          `# add ${toSnakeCase(topic.name)}_callback implementation below\n`
        ) &&
        customTextStr.includes(
          `\n        # end ${toSnakeCase(topic.name)}_callback implementation`
        )
      ) {
        const callbackText = customTextStr
          .slice(
            getLastIndexOf(
              customTextStr,
              `# add ${toSnakeCase(topic.name)}_callback implementation below\n`
            ),
            customTextStr.indexOf(
              `\n        # end ${toSnakeCase(
                topic.name
              )}_callback implementation`
            )
          )
          .trimEnd();
        const callbackObj = customNodeText.callbacks.find(
          (callback) =>
            callback.name === `${toSnakeCase(topic?.name!)}_callback`
        );
        if (callbackObj) {
          callbackObj.text = callbackText;
        } else {
          customNodeText.callbacks.push({
            name: `${toSnakeCase(topic.name)}_callback`,
            text: callbackText
          });
        }
      }
    }
  }
  // getting service server callback text
  getNodeServiceDependencies(workspaceId, node.id).servers.forEach(
    (srvInfo) => {
      if (
        customTextStr.includes(
          `# add ${toSnakeCase(
            srvInfo.srv.name
          )}_srv_callback implementation below\n`
        ) &&
        customTextStr.includes(
          `\n        # end ${toSnakeCase(
            srvInfo.srv.name
          )}_srv_callback implementation`
        )
      ) {
        customNodeText.callbacks.push({
          name: `${toSnakeCase(srvInfo.srv.name)}_srv_callback`,
          text: customTextStr
            .slice(
              getLastIndexOf(
                customTextStr,
                `# add ${toSnakeCase(
                  srvInfo.srv.name
                )}_srv_callback implementation below\n`
              ),
              customTextStr.indexOf(
                `\n        # end ${toSnakeCase(
                  srvInfo.srv.name
                )}_srv_callback implementation`
              )
            )
            .trimEnd()
        });
      }
    }
  );
  return customNodeText;
};
